







































































































































































































































































































import { computed, defineComponent, PropType, useContext, useRoute } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash-es';
import ProductCartButton from '~/components/pages/Product/ProductCartButton.vue';
import ProductStyleLabel from '~/components/pages/Product/ProductStyleLabel.vue';
import ProductWineColorLabel from '~/components/pages/Product/ProductWineColorLabel.vue';
import AppClamp from '~/components/partials/App/AppClamp.vue';
import AppFavoriteIconButton from '~/components/partials/App/AppFavoriteIconButton.vue';
import AppFavoriteTextButton from '~/components/partials/App/AppFavoriteTextButton.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppPrice from '~/components/partials/App/AppPrice.vue';
import AppRectButton from '~/components/partials/App/AppRectButton.vue';
import ProducingAreaFlag from '~/components/partials/ProducingArea/ProducingAreaFlag.vue';
import ProductLabel from '~/components/partials/Product/ProductLabel.vue';
import { useListFavorite } from '~/composables/contents/useListFavorite';
import { useProductParams } from '~/composables/product/useProductParams';
import { useEnotecaApp } from '~/composables/useEnotecaApp';
import { useStore } from '~/composables/useStore';
import { PRODUCT_CATEGORY_ID, PRODUCT_CART_BUTTON, REVIEW_HASH } from '~/constants';
import { BOTTLE_200 } from '~/constants/profilesForImage';
import { isInsidePeriod, nonNullable, url } from '~/utils';
import { getLotteryInfoFromSearch } from '~/utils/checkLotteryStatus';
import { convertColorText, filterReviewRating } from '~/utils/modules/products';
import { ProductListItem } from '~/types/modules/product';
import { DisplayLang, FavoriteTypes, ProductCategoryText, ProductParentCategory, ProductSetType } from '~/enums';

export default defineComponent({
  components: {
    AppIcon,
    AppPrice,
    AppImage,
    AppLink,
    AppClamp,
    AppRectButton,
    AppFavoriteTextButton,
    AppFavoriteIconButton,
    ProductLabel,
    ProducingAreaFlag,
    ProductWineColorLabel,
    ProductStyleLabel,
    ProductCartButton,
  },
  props: {
    isProductVariantUnit: {
      type: Boolean,
      required: false,
    },
    item: {
      type: Object as PropType<ProductListItem>,
      required: true,
    },
    langCode: {
      type: String as PropType<DisplayLang>,
      default: DisplayLang.JA,
    },
    isDynamicParts: {
      type: Boolean,
      default: false,
    },
    /** ダイナミックパーツで指定された子商品 */
    variantCodes: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /** 短縮版キャッチを表示するか（しない場合は通常の3行キャッチ） */
    showShorterCatch: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    addToCard: (itemCode: string) => typeof itemCode === 'string',
  },
  setup(props) {
    const { i18n } = useContext();
    const route = useRoute();
    const store = useStore();
    const { category } = useProductParams();
    const { checkIsFavorite } = useListFavorite();
    const { isEnotecaAppWebView } = useEnotecaApp();
    const isFavorite = computed(() => checkIsFavorite(props.item.code, FavoriteTypes.ITEM));
    /** アプリかつお気に入り一覧（アプリのネイティブ画面から導線あり）のときは、WebViewを起動させるために外部リンク（aタグ）にする */
    const isExternalLink = computed(() => isEnotecaAppWebView.value && route.value.path === url('FAVORITE_LIST'));

    // SSR時にundefined要素が入るという謎挙動があるためfilterをかける（不正子商品が指定された単品ワインカードダイナミックパーツ）
    const productVariantCodes = computed(() => props.variantCodes.filter(nonNullable));

    const productDetailUrl = computed(() => {
      return url(
        props.item.product_type &&
          props.item.product_type === i18n.t('Common.productListItem.text.productType').toString()
          ? 'ITEM_DISTRIBUTION_DETAIL'
          : 'ITEM_DETAIL',
        { code: props.item.code }
      );
    });

    // 子商品id
    // 抽選詳細画面でのみ子商品単位で表示するために使用
    const lotteryItemId = computed(() =>
      props.isProductVariantUnit && props.item.raffle ? props.item.raffle[0].raffleItemId : undefined
    );

    // 子商品コード
    // 抽選詳細画面でのみ小商品情報を表示するために使用
    const lotteryItemCode = computed(() =>
      props.isProductVariantUnit && props.item.raffle ? props.item.raffle[0].productVariantCode : undefined
    );

    const lottery = computed(() => getLotteryInfoFromSearch(props.item));

    const reviewLink = computed(() => url('ITEM_DETAIL', { code: props.item.code }) + REVIEW_HASH);

    const isNowOnSale = computed(() => {
      if (itemChildren.value.length === 1) {
        return itemChildren.value[0].isNowOnSale;
      }
      return isInsidePeriod(props.item.sale_start, props.item.sale_end);
    });

    const hasStock = computed(() => {
      if (itemChildren.value.length === 1) {
        return itemChildren.value[0].stock > 0;
      }
      return props.item.has_stock;
    });

    const isWine = computed(
      () =>
        category.value &&
        [ProductParentCategory.WINE, ProductParentCategory.OTHER, ProductParentCategory.NON_ALCOHOL].includes(
          category.value
        ) &&
        props.item.product_type !== i18n.t('Common.productListItem.text.productType').toString()
    );

    // よりどり商品かどうか
    const isFlexibleSetItem = computed(() => {
      return props.item.set_type === ProductSetType.FLEXIBLE_SET;
    });

    // 頒布会商品かどうか
    const isDistributionItem = computed(() => {
      return props.item.product_type === i18n.t('Common.productListItem.text.productType').toString();
    });

    // 特価価格かどうか
    const isSpecialPrice = computed(() => {
      return typeof props.item.discount_rate === 'number';
    });

    // 商品詳細リンクを設定するか
    const isUnsetItemDetailLink = computed(() => {
      // お気に入り一覧ページかつ、親商品非表示設定かつ、頒布会商品であるか判定
      const isDisplay = !!props.item.product_display_flag;
      return !isDisplay && route.value.path === url('FAVORITE_LIST') && !isDistributionItem.value;
    });

    // 最短出荷日を表示するか
    const showEarliestShippingDate = computed(() => {
      return (
        props.item.single_display_variant_flag &&
        props.item.earliest_shipping_date &&
        dayjs(props.item.earliest_shipping_date).isValid() &&
        !props.item.d1_flag
      );
    });

    const colorText = computed(() => convertColorText(props.item.color));

    const producingArea = computed(() => {
      const { producing_area } = props.item;

      return {
        code_id: producing_area.area_id_1 || '',
        areas: [
          {
            name: producing_area.area_1,
            id: producing_area.area_id_1,
          },
          {
            name: producing_area.area_2,
            id: producing_area.area_id_2,
          },
          {
            name: producing_area.area_3,
            id: producing_area.area_id_3,
          },
        ],
      };
    });

    /** 子商品 */
    const itemChildren = computed(() => {
      // 生産年_表示価格_新着フラグ_定価_在庫数_販売開始日時_販売終了日時_最高得点の評価誌種別_最高得点の評価誌ポイント_表示フラグ_子商品コード
      return props.item.children
        .map((el: string) => {
          const child = el.split('_');
          const code = child[0].replace('年', '');
          const specialPrice = Number(child[1].replace('円', ''));
          const isNew = Number(child[2]);
          const price = Number(child[3].replace('円', ''));
          const stock = Number(child[4]);
          const isNowOnSale = isInsidePeriod(child[5], child[6]);
          const mediaType = child[7];
          const mediaRating = child[8];
          const isDisplay = Number(child[9]);

          // 抽選詳細などの子商品単位で出す箇所は非表示の商品も出す
          if (!props.isProductVariantUnit && !isDisplay) return null;

          return {
            code,
            price,
            isNew,
            specialPrice,
            stock,
            isNowOnSale,
            mediaType,
            mediaRating,
          };
        })
        .filter(nonNullable)
        .sort((a, b) => Number(b.code) - Number(a.code));
    });

    const isVisaMember = computed(() => store.getters['user/info/isVisaMember']);

    const labels = computed(() => {
      const {
        sale_label,
        is_limited_stock,
        is_visa_happy_wine,
        bulk_buying_promotion,
        bulk_buying_promotion_valid_from,
        bulk_buying_promotion_valid_to,
        d1_flag,
      } = props.item;

      const bulkBuyingPromotionValidFroms = bulk_buying_promotion_valid_from?.split('_') || [];
      const bulkBuyingPromotionValidTos = bulk_buying_promotion_valid_to?.split('_') || [];

      const bulkBuyingPromotions =
        uniqBy(
          bulk_buying_promotion?.split('_').map((bulkBuyingPromotion, index) => {
            // dayjs.isBetween はタイムゾーンが揃っていないと意図しない挙動になるので Asia/Tokyo に統一
            const bulkBuyingPromotionValidFrom = dayjs(bulkBuyingPromotionValidFroms[index]).tz('Asia/Tokyo');
            const bulkBuyingPromotionValidTo = dayjs(bulkBuyingPromotionValidTos[index]).tz('Asia/Tokyo');

            if (!dayjs().tz('Asia/Tokyo').isBetween(bulkBuyingPromotionValidFrom, bulkBuyingPromotionValidTo))
              return null;

            return { color: 'Sale', text: bulkBuyingPromotion };
          }),
          'text'
        ).filter(nonNullable) || [];

      return [
        is_limited_stock
          ? { outline: 'Gray', text: i18n.t('Common.productListItem.text.limitedStock').toString() }
          : null,
        sale_label ? { color: 'Sale', text: sale_label } : null,
        isVisaMember.value && is_visa_happy_wine
          ? { color: 'Sale', text: i18n.t('Common.productListItem.text.visaHappyWine').toString() }
          : null,
        ...bulkBuyingPromotions,
        d1_flag && store.state.modules.product.shippingStatus.isShippable
          ? { outline: 'Gray', text: i18n.t('Item-03.label.expeditedShipping').toString(), icon: 'ExpeditedShipping' }
          : null,
      ].filter(nonNullable);
    });

    const labelsOnImage = computed(() => {
      const { is_free_shipping, discount_rate } = props.item;

      return [
        discount_rate
          ? {
              category: 'discount',
              title: i18n.t('Common.productListItem.text.discount', { discountRate: discount_rate }).toString(),
            }
          : null,
        is_free_shipping
          ? { category: 'freeShipping', title: i18n.t('Common.productListItem.text.freeShipping').toString() }
          : null,
      ].filter(nonNullable);
    });

    const productImage = computed(() => {
      const { image, category_ids } = props.item;
      if (image) return image + BOTTLE_200;

      if (category_ids.includes(`${PRODUCT_CATEGORY_ID.WINE_CELLAR}`)) {
        return require('~/assets/images/no-image/cellar.png');
      }
      if (category_ids.includes(`${PRODUCT_CATEGORY_ID.SNACK}`)) {
        return require('~/assets/images/no-image/food.png');
      }
      if (category_ids.includes(`${PRODUCT_CATEGORY_ID.WINE_GOODS}`)) {
        return require('~/assets/images/no-image/goods.png');
      }
      return require('~/assets/images/no-image/wine.png');
    });

    /**
     * 現在表示している子商品コードに一致する、味わいとヴィンテージ情報2のテキストを結合する
     * (「子商品コード:」が文頭に入って連携されるため、全て削除してから文字列を返している)
     */
    const tasteAndVintageText = computed(() => {
      const tasteText = props.item.taste_reviews.find((tasteInfo) => tasteInfo.includes(`${lotteryItemCode.value}:`));
      const vintageText = props.item.vintage_info_2_list.find((vintageInfo) =>
        vintageInfo.includes(`${lotteryItemCode.value}:`)
      );
      return `${tasteText ?? ''}${vintageText ?? ''}`.replaceAll(`${lotteryItemCode.value}:`, '');
    });

    return {
      getLotteryInfoFromSearch,
      reviewLink,
      url,
      isNowOnSale,
      hasStock,
      ProductCategoryText,
      productDetailUrl,
      isWine,
      isFlexibleSetItem,
      isDistributionItem,
      producingArea,
      colorText,
      isFavorite,
      itemChildren,
      productVariantCodes,
      labelsOnImage,
      labels,
      productImage,
      DisplayLang,
      category,
      ProductParentCategory,
      isSpecialPrice,
      PRODUCT_CART_BUTTON,
      isUnsetItemDetailLink,
      isExternalLink,
      filterReviewRating,
      dayjs,
      showEarliestShippingDate,
      lotteryItemId,
      lottery,
      tasteAndVintageText,
    };
  },
});
